import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeStore, useAuthStore } from '@/stores';
import { usePermission } from '@/hooks/usePermission';
import {
  Sun,
  Moon,
  User,
  Info,
  Wifi,
  WifiOff,
  Settings,
  Headphones,
  Mail,
  Copy,
  Check,
  Send,
  Clock,
  ShieldCheck,
  Radio,
  LogOut
} from 'lucide-react';
import { supabase } from '@/utils/supabase';
import { useLogout } from '@/hooks/useAuth';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, language, setTheme, setLanguage } = useThemeStore();
  const storeUser = useAuthStore((s) => s.user);
  const storeAuth = useAuthStore((s) => s.isAuthenticated);
  const storePartnerId = useAuthStore((s) => s.partnerId);
  const user = storeUser ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().user : null);
  const isAuthenticated = storeAuth ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().isAuthenticated : false);
  const partnerId = storePartnerId ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().partnerId : null);

  const { logout } = useLogout();
  const { hasPermission } = usePermission();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string>(() => {
    const currentUser = user ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().user : null);
    if (currentUser?.email && !currentUser.email.endsWith('@ftth.iq')) {
      return currentUser.email;
    }
    return typeof window !== 'undefined' ? (localStorage.getItem('google_user_email') || '') : '';
  });

  React.useEffect(() => {
    let isMounted = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMounted && session?.user?.email) {
        setSessionEmail(session.user.email);
        try {
          localStorage.setItem('google_user_email', session.user.email);
        } catch {}
      }
    }).catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('support@ftthnexus.com');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const toggleLanguage = (lang: 'ar' | 'en') => {
    setLanguage(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  const isRTL = language === 'ar';
  const isLight = theme === 'light';
  
  const currentUser = user ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().user : null);
  const displayName = currentUser?.firstName ? `${currentUser.firstName} ${currentUser.lastName || ''}`.trim() : 'مقاول';
  const connectionMode = isAuthenticated ? 'الوضع الحقيقي' : 'وضع تجريبي';
  const displayPartnerId = partnerId ? `PARTNER-${partnerId}` : 'PARTNER-001';
  // Canonical email priority:
  // 1. Live active Supabase session email (cannot be overridden by stale cache)
  // 2. Canonical user.email from auth store (if genuine and not ending with @ftth.iq)
  // 3. Cached identity from localStorage (google_user_email)
  const accountEmail = sessionEmail ||
    (currentUser?.email && !currentUser.email.endsWith('@ftth.iq') ? currentUser.email : '') ||
    (typeof window !== 'undefined' ? localStorage.getItem('google_user_email') : '') ||
    '—';

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%', maxWidth: '700px', margin: '0 auto' }}>
      {/* Page title */}
      <h1 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '22px', fontWeight: 700, marginBottom: '24px', textAlign: 'right' }}>
        {t('settings.title') || 'الإعدادات'}
      </h1>

      {/* Display Settings Section */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '16px',
          boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', justifyContent: 'flex-end', direction: 'rtl' }}>
          <h2 style={{ color: isLight ? '#1e293b' : '#e2e8f0', fontSize: '16px', fontWeight: 700 }}>
            {t('settings.displaySettings') || 'إعدادات العرض'}
          </h2>
          <Settings size={18} color="#3b82f6" />
        </div>

        {/* Language */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'right', marginBottom: '10px' }}>
            {t('settings.language') || 'اللغة'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <button
              onClick={() => toggleLanguage('en')}
              style={{
                padding: '11px',
                borderRadius: '8px',
                border: '1px solid transparent',
                background: language === 'en' ? 'linear-gradient(90deg, #3b82f6, #6366f1)' : (isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'),
                color: language === 'en' ? '#fff' : (isLight ? '#475569' : '#64748b'),
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s',
                minHeight: '44px',
              }}
            >
              الإنجليزية (EN)
            </button>
            <button
              onClick={() => toggleLanguage('ar')}
              style={{
                padding: '11px',
                borderRadius: '8px',
                border: '1px solid transparent',
                background: language === 'ar' ? 'linear-gradient(90deg, #3b82f6, #6366f1)' : (isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'),
                color: language === 'ar' ? '#fff' : (isLight ? '#475569' : '#64748b'),
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s',
                minHeight: '44px',
              }}
            >
              العربية (عربي)
            </button>
          </div>
        </div>

        {/* Theme */}
        <div>
          <div style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'right', marginBottom: '10px' }}>
            {t('settings.theme') || 'المظهر'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <button
              onClick={() => setTheme('light')}
              style={{
                padding: '11px',
                borderRadius: '8px',
                border: '1px solid transparent',
                background: theme === 'light' ? 'linear-gradient(90deg, #3b82f6, #6366f1)' : (isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'),
                color: theme === 'light' ? '#fff' : (isLight ? '#475569' : '#64748b'),
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                minHeight: '44px',
              }}
            >
              <Sun size={16} />
              الوضع الفاتح
            </button>
            <button
              onClick={() => setTheme('dark')}
              style={{
                padding: '11px',
                borderRadius: '8px',
                border: '1px solid transparent',
                background: theme === 'dark' ? 'linear-gradient(90deg, #3b82f6, #6366f1)' : (isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'),
                color: theme === 'dark' ? '#fff' : (isLight ? '#475569' : '#64748b'),
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                minHeight: '44px',
              }}
            >
              <Moon size={16} />
              الوضع الداكن
            </button>
          </div>
        </div>
      </div>

      {/* Users & Permissions Section */}
      {hasPermission('users.manage') && (
        <div
          style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '16px',
            boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', direction: 'rtl' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldCheck size={18} color="#3b82f6" />
              <h2 style={{ color: isLight ? '#1e293b' : '#e2e8f0', fontSize: '16px', fontWeight: 700, margin: 0 }}>
                Users & Permissions
              </h2>
            </div>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>إدارة المستخدمين والصلاحيات</span>
          </div>
          <p style={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '13px', textAlign: 'right', marginBottom: '16px', lineHeight: 1.5 }}>
            عرض صفحة المستخدمين والصلاحيات المستقلة في الإعدادات لتحديد أدوار ومستويات الوصول.
          </p>
          <button
            onClick={() => navigate('/settings/users-permissions')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
              color: '#ffffff',
              padding: '10px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
            }}
          >
            <ShieldCheck size={16} />
            <span>الانتقال لصفحة Users & Permissions</span>
          </button>
        </div>
      )}

      {/* Account Info Section */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '16px',
          boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', justifyContent: 'flex-end' }}>
          <h2 style={{ color: isLight ? '#1e293b' : '#e2e8f0', fontSize: '16px', fontWeight: 700 }}>
            {t('settings.accountInfo') || 'معلومات الحساب'}
          </h2>
          <User size={18} color="#3b82f6" />
        </div>

        <div className="settings-info-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <InfoField label="Display Name" value={displayName} isLight={isLight} />
          <InfoField label={t('settings.username') || 'اسم المستخدم'} value={user?.username || '—'} isLight={isLight} />
          <InfoField label={t('settings.email') || 'البريد الإلكتروني'} value={accountEmail} isLight={isLight} fullWidth />
          <InfoField label="Mode" value={connectionMode} isLight={isLight} />
          <InfoField label="Partner ID" value={displayPartnerId} isLight={isLight} />
        </div>

        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-start' }}>
          <button
            onClick={logout}
            type="button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '8px',
              background: isLight ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.15)',
              border: isLight ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.4)',
              color: isLight ? '#dc2626' : '#f87171',
              cursor: 'pointer',
              fontSize: '13.5px',
              fontWeight: 700,
              transition: 'all 0.2s ease',
            }}
          >
            <LogOut size={16} />
            <span>{t('auth.logout') || 'تسجيل الخروج'}</span>
          </button>
        </div>
      </div>

      {/* Connection Status Section */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '16px',
          boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', justifyContent: 'flex-end' }}>
          <h2 style={{ color: isLight ? '#1e293b' : '#e2e8f0', fontSize: '16px', fontWeight: 700 }}>
            {t('settings.connectionStatus') || 'حالة الاتصال'}
          </h2>
          <Info size={18} color="#3b82f6" />
        </div>

        {isAuthenticated ? (
          <div
            style={{
              background: isLight ? '#ecfdf5' : 'rgba(16,185,129,0.1)',
              border: isLight ? '1px solid #a7f3d0' : '1px solid rgba(16,185,129,0.2)',
              borderRadius: '10px',
              padding: '16px 20px',
              direction: 'rtl',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginBottom: '10px' }}>
              <span style={{ color: isLight ? '#059669' : '#34d399', fontSize: '15px', fontWeight: 700 }}>متصل بالشبكة الوطنية</span>
              <Wifi size={16} color={isLight ? '#059669' : '#34d399'} />
            </div>
            <div style={{ color: isLight ? '#475569' : '#64748b', fontSize: '12px', textAlign: 'right', marginBottom: '12px' }}>
              admin.ftth.iq — Connected
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: isLight ? '#64748b' : '#475569', fontSize: '11px' }}>Partner ID: </span>
                <span style={{ color: isLight ? '#334155' : '#64748b', fontSize: '11px', fontWeight: 600 }}>{partnerId}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: isLight ? '#64748b' : '#475569', fontSize: '11px' }}>Base URL: </span>
                <span style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 600 }}>https://admin.ftth.iq</span>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              background: isLight ? '#fffbeb' : 'rgba(180,83,9,0.1)',
              border: isLight ? '1px solid #fde68a' : '1px solid rgba(251,191,36,0.2)',
              borderRadius: '10px',
              padding: '16px 20px',
              direction: 'rtl',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', marginBottom: '10px' }}>
              <span style={{ color: isLight ? '#d97706' : '#fbbf24', fontSize: '15px', fontWeight: 700 }}>وضع تجريبي</span>
              <WifiOff size={16} color={isLight ? '#d97706' : '#fbbf24'} />
            </div>
            <div style={{ color: isLight ? '#64748b' : '#64748b', fontSize: '12px', textAlign: 'right', marginBottom: '12px' }}>
              admin.ftth.iq — Not connected
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: isLight ? '#64748b' : '#475569', fontSize: '11px' }}>Client App: </span>
                <span style={{ color: isLight ? '#334155' : '#64748b', fontSize: '11px' }}>53d57a7f-3f89-4e9d-873b-3d071bc6dd9f</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: isLight ? '#64748b' : '#475569', fontSize: '11px' }}>Base URL: </span>
                <span style={{ color: '#3b82f6', fontSize: '11px' }}>https://admin.ftth.iq</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS Micro-Animations for Support Section */}
      <style>{`
        @keyframes supportPulseGlow {
          0% { transform: scale(1); box-shadow: 0 4px 14px rgba(59,130,246,0.35); }
          50% { transform: scale(1.06); box-shadow: 0 6px 22px rgba(99,102,241,0.55); }
          100% { transform: scale(1); box-shadow: 0 4px 14px rgba(59,130,246,0.35); }
        }
        @keyframes supportLivePing {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(2.2); opacity: 0; }
        }
        .support-card-hover {
          transition: all 0.25s ease-in-out;
        }
        .support-card-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(59,130,246,0.15);
        }
      `}</style>

      {/* Technical Support Section (الدعم الفني) */}
      <div
        className="support-card-hover"
        style={{
          background: isLight
            ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            : 'linear-gradient(135deg, rgba(13,31,60,0.85) 0%, rgba(30,41,59,0.75) 100%)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: isLight ? '0 4px 16px rgba(0,0,0,0.04)' : '0 8px 32px rgba(0,0,0,0.25)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Ambient Radial Background Glow */}
        <div
          style={{
            position: 'absolute',
            top: '-50px',
            left: '-50px',
            width: '140px',
            height: '140px',
            background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, rgba(0,0,0,0) 70%)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }}
        />

        {/* Section Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', direction: 'rtl' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                animation: 'supportPulseGlow 3s infinite ease-in-out',
                boxShadow: '0 4px 14px rgba(59,130,246,0.35)'
              }}
            >
              <Headphones size={22} />
            </div>
            <div>
              <h2 style={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '18px', fontWeight: 800, margin: 0 }}>
                الدعم الفني
              </h2>
              <span style={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '12px' }}>
                المساعدة المباشرة والاستفسارات للمشروع الوطني FTTH
              </span>
            </div>
          </div>

          {/* 24/7 Status Badge */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: isLight ? '#ecfdf5' : 'rgba(16,185,129,0.12)',
              border: isLight ? '1px solid #a7f3d0' : '1px solid rgba(16,185,129,0.25)',
              padding: '5px 12px',
              borderRadius: '20px',
              color: '#10b981',
              fontSize: '11.5px',
              fontWeight: 700,
              flexShrink: 0
            }}
          >
            <span style={{ position: 'relative', display: 'flex', width: '8px', height: '8px' }}>
              <span style={{ position: 'absolute', display: 'inline-flex', width: '100%', height: '100%', borderRadius: '50%', background: '#10b981', animation: 'supportLivePing 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
              <span style={{ position: 'relative', display: 'inline-flex', borderRadius: '50%', width: '8px', height: '8px', background: '#10b981' }} />
            </span>
            متاح 24/7
          </div>
        </div>

        {/* Support Email Card */}
        <div
          style={{
            background: isLight ? '#f8fafc' : 'rgba(15,23,42,0.6)',
            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '16px 18px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '14px',
            direction: 'rtl'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#3b82f6',
                flexShrink: 0
              }}
            >
              <Mail size={22} />
            </div>
            <div>
              <div style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 600, marginBottom: '2px' }}>البريد الإلكتروني المباشر للدعم الفني</div>
              <a
                href="mailto:support@ftthnexus.com"
                style={{
                  fontSize: '15.5px',
                  fontWeight: 800,
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontFamily: 'monospace',
                  direction: 'ltr',
                  display: 'inline-block'
                }}
              >
                support@ftthnexus.com
              </a>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Copy Email Button */}
            <button
              onClick={handleCopyEmail}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: copiedEmail ? '#10b981' : (isLight ? '#ffffff' : 'rgba(255,255,255,0.08)'),
                color: copiedEmail ? '#ffffff' : (isLight ? '#334155' : '#e2e8f0'),
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '8px 14px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: copiedEmail ? '0 2px 10px rgba(16,185,129,0.35)' : 'none'
              }}
            >
              {copiedEmail ? <Check size={15} /> : <Copy size={15} />}
              {copiedEmail ? 'تم النسخ!' : 'نسخ'}
            </button>

            {/* Direct Mailto Send Button */}
            <a
              href="mailto:support@ftthnexus.com?subject=طلب%20دعم%20فني%20-%20مشروع%20FTTH%20Nexus"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: '#ffffff',
                borderRadius: '8px',
                padding: '8px 14px',
                fontSize: '12.5px',
                fontWeight: 700,
                textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              <Send size={15} />
              إرسال رسالة
            </a>
          </div>
        </div>

        {/* Highlights Row (Response time + Encryption security) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', direction: 'rtl' }}>
          <div
            style={{
              background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
              border: isLight ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.06)',
              borderRadius: '10px',
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <Clock size={18} style={{ color: '#6366f1', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>معدل الاستجابة</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: isLight ? '#0f172a' : '#e2e8f0' }}>أقل من 15 دقيقة</div>
            </div>
          </div>

          <div
            style={{
              background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
              border: isLight ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.06)',
              borderRadius: '10px',
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <ShieldCheck size={18} style={{ color: '#10b981', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>حماية وخصوصية</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: isLight ? '#0f172a' : '#e2e8f0' }}>دعم مشفر وآمن</div>
            </div>
          </div>
        </div>
      </div>

      {/* About App Section */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', justifyContent: 'flex-end' }}>
          <h2 style={{ color: isLight ? '#1e293b' : '#e2e8f0', fontSize: '16px', fontWeight: 700 }}>
            {t('settings.aboutApp') || 'حول التطبيق'}
          </h2>
          <Info size={18} color="#3b82f6" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', direction: 'rtl' }}>
          <div style={{ flex: 1 }}>
            <div className="settings-about-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <InfoField label={t('settings.version') || 'الإصدار'} value="1.0.0" isLight={isLight} />
              <InfoField label="Build" value="2025.02" isLight={isLight} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: '#ffffff',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <img src="/logo.png" alt="FTTH Nexus Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontWeight: 700, fontSize: '14px' }}>FTTH Nexus</div>
              <div style={{ color: '#64748b', fontSize: '10px' }}>National FTTH Project Contractor Portal</div>
            </div>
          </div>
        </div>
        <hr style={{ border: 'none', borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', margin: '16px 0 12px 0' }} />
        <div style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '12px', fontWeight: 600, textAlign: 'center', direction: 'ltr' }}>
          All Rights Reserved City Telecom. By Haider Ali
        </div>
      </div>
    </div>
  );
};

const InfoField: React.FC<{ label: string; value: string; isLight?: boolean; fullWidth?: boolean }> = ({ label, value, isLight, fullWidth }) => (
  <div
    style={{
      direction: 'rtl',
      background: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
      border: isLight ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: '10px',
      padding: '10px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      ...(fullWidth ? { gridColumn: '1 / -1' } : {}),
    }}
  >
    <div style={{ color: '#64748b', fontSize: '11px', fontWeight: 600, textAlign: 'right' }}>{label}</div>
    <div style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '14px', fontWeight: 700, textAlign: 'right', wordBreak: 'break-all' }}>{value}</div>
  </div>
);

export default SettingsPage;